import { useEffect, useMemo, useState } from 'react';
import { Layout, LayoutItem } from 'react-grid-layout';

import { DashboardWidgetType } from '../components/dashboard/Dashboard.types';

const storageKey = 'dashboard.widgets';

const loadWidgetsFromStorage = (): DashboardWidgetType[] => {
 if (typeof window === 'undefined') {
  return [];
 }
 try {
  const saved = localStorage.getItem(storageKey);
  if (saved) {
   const parsed = JSON.parse(saved) as DashboardWidgetType[];
   console.log('Loaded widgets from localStorage on mount', parsed);
   return parsed;
  }
 } catch (error) {
  console.error('Failed to load widgets from localStorage on mount', error);
 }
 return [];
};

const createWidgetWithLayout = (
 data: DashboardWidgetType,
 currentWidgets: DashboardWidgetType[],
): DashboardWidgetType => {
 const widgetWidth = data.w ?? 2;
 const maxY = Math.max(
  ...currentWidgets.map((w) => (w.y ?? 0) + (w.h ?? 2)),
  -1,
 );
 const totalColumns = 6;

 // First, try to find space in existing rows
 let newY = maxY + 1;
 let newX = 0;
 let foundSpace = false;

 // Check each row from 0 to maxY to find available space
 for (let y = 0; y <= maxY; y += 1) {
  // Get all widgets that occupy this row (considering their height)
  const occupiedPositions = currentWidgets
   .filter((w) => {
    const wY = w.y ?? 0;
    const wH = w.h ?? 2;
    // Widget occupies this row if it starts at or before this row and extends to or beyond this row
    return wY <= y && wY + wH > y;
   })
   .map((w) => ({ x: w.x ?? 0, w: w.w ?? 2 }));

  // Try to find a position in this row
  for (let x = 0; x <= totalColumns - widgetWidth; x += 1) {
   const wouldOverlap = occupiedPositions.some(
    (pos) => x < pos.x + pos.w && x + widgetWidth > pos.x,
   );
   if (!wouldOverlap) {
    newX = x;
    newY = y;
    foundSpace = true;
    break;
   }
  }
  if (foundSpace) {
   break;
  }
 }

 // If no space found in existing rows, place in new row
 if (!foundSpace) {
  newY = maxY + 1;
  newX = 0;
 }
 console.log(widgetWidth, data.minW);
 return {
  ...data,
  id: data.id && data.id !== '-1' ? data.id : Date.now().toString(),
  x: newX,
  y: newY,
  w: widgetWidth,
  h: data.h ?? 2,
  minW: data.minW ?? 2,
  maxW: data.maxW ?? 6,
  minH: data.minH ?? 2,
  maxH: data.maxH,
 };
};

const useDashboard = () => {
 const [widgets, setWidgets] = useState<DashboardWidgetType[]>(
  loadWidgetsFromStorage,
 );
 const [modalOpen, setModalOpen] = useState(false);
 const [editingWidget, setEditingWidget] = useState<DashboardWidgetType | null>(
  null,
 );

 useEffect(() => {
  if (typeof window === 'undefined') {
   return;
  }
  try {
   localStorage.setItem(storageKey, JSON.stringify(widgets));
   console.log('Saved widgets to localStorage', widgets);
  } catch (error) {
   console.error('Failed to persist widgets to localStorage', error);
  }
 }, [widgets]);

 const layout = useMemo(
  () =>
   widgets.map((widget) => ({
    i: widget.id || '-1',
    x: widget.x ?? 0,
    y: widget.y ?? 0,
    w: widget.w ?? 3,
    h: widget.h ?? 2,
    minW: widget.minW ?? 2,
    maxW: widget.maxW ?? 6,
    minH: widget.minH ?? 2,
    maxH: widget.maxH,
   })),
  [widgets],
 );

 const handleLayoutChange = (currentLayout: Layout) => {
  const layoutToUse = currentLayout;
  setWidgets((prevWidgets) =>
   prevWidgets.map((widget) => {
    const layoutItem = layoutToUse.find((item) => item.i === widget.id);

    if (layoutItem) {
     return {
      ...widget,
      x: layoutItem.x,
      y: layoutItem.y,
      w: layoutItem.w,
      h: layoutItem.h,
     };
    }
    return widget;
   }),
  );
 };

 const handleUpsertWidget = (data: DashboardWidgetType) => {
  setWidgets((prevWidgets) => {
   const now = Date.now();
   if (data.id && prevWidgets.some((widget) => widget.id === data.id)) {
    return prevWidgets.map((widget) =>
     widget.id === data.id ? { ...widget, ...data, lastUpdated: now } : widget,
    );
   }
   const newWidget = createWidgetWithLayout(data, prevWidgets);
   return [...prevWidgets, { ...newWidget, lastUpdated: now }];
  });
 };

 const handleDelete = (id: string) => {
  setWidgets((prevWidgets) => prevWidgets.filter((w) => w.id !== id));
 };

 const handleEdit = (widget: DashboardWidgetType) => {
  setEditingWidget(widget);
  setModalOpen(true);
 };

 const handleOpenCreateModal = () => {
  setEditingWidget(null);
  setModalOpen(true);
 };

 const handleModalClose = () => {
  setModalOpen(false);
  setEditingWidget(null);
 };

 return {
  widgets,
  layout,
  modalOpen,
  editingWidget,
  handleLayoutChange,
  handleUpsertWidget,
  handleDelete,
  handleEdit,
  handleOpenCreateModal,
  handleModalClose,
 };
};

export default useDashboard;
